/**
 * 《薪火大地》世界地图 v1
 *
 * 合规红线（GDD 12.1 / 美术风格指南 §5）：
 * **不出现任何真实国界、真实国家名**。全部区域均为风格化虚构划分，
 * 只借用"地形—气候—人口"的抽象关系承载地理与历史教学语义。
 *
 * 布局分四块：
 * - 西陆（r-hearth … r-lagoonisle）  第一章「薪火初燃」使用
 * - 中陆（r-monsoonbay …）           第二章「陆海通途」使用（季风/洋流）
 * - 东陆（r-amberplain …）           第三章「文明之光」使用（四大发明西传）
 * - 极地（r-frostplain / r-auroracoast）
 *
 * 关卡只启用其中的区域子集（见 `levels/chapter1.ts`），
 * 但模拟核心永远拿到完整地图数据，服务端复算才有一致输入。
 */

import type { WorldMap } from "../types.js";

export const WORLD_MAP_V1: WorldMap = {
  id: "world-v1",
  name: "薪火大地",
  regions: [
    // ---------- 西陆（第一章） ----------
    { id: "r-hearth", name: "炉火原", terrain: "plain", population: 120, initialEnlightenment: 20, x: 0.19, y: 0.28 },
    { id: "r-riverland", name: "双河沃野", terrain: "plain", population: 150, initialEnlightenment: 12, x: 0.11, y: 0.41 },
    { id: "r-pineforest", name: "松涛密林", terrain: "forest", population: 70, initialEnlightenment: 6, x: 0.17, y: 0.55 },
    { id: "r-cloudridge", name: "云岭高原", terrain: "mountain", population: 55, initialEnlightenment: 5, x: 0.09, y: 0.67 },
    { id: "r-saltflat", name: "盐湖荒漠", terrain: "desert", population: 40, initialEnlightenment: 2, x: 0.26, y: 0.5 },
    { id: "r-stonegate", name: "石门关隘", terrain: "mountain", population: 45, initialEnlightenment: 4, x: 0.18, y: 0.79 },
    { id: "r-longcoast", name: "长滩海岸", terrain: "coast", population: 90, initialEnlightenment: 8, x: 0.3, y: 0.87 },
    { id: "r-lagoonisle", name: "环礁小岛", terrain: "coast", population: 35, initialEnlightenment: 1, x: 0.41, y: 0.94 },

    // ---------- 中陆（第二章：季风、洋流、人口） ----------
    { id: "r-monsoonbay", name: "季风湾", terrain: "coast", population: 130, initialEnlightenment: 3, x: 0.47, y: 0.73 },
    { id: "r-spiceisle", name: "香料群岛", terrain: "coast", population: 60, initialEnlightenment: 2, x: 0.54, y: 0.88 },
    { id: "r-jadelake", name: "碧玉湖", terrain: "plain", population: 85, initialEnlightenment: 4, x: 0.45, y: 0.3 },
    { id: "r-sunplateau", name: "星象高原", terrain: "mountain", population: 50, initialEnlightenment: 3, x: 0.58, y: 0.58 },
    { id: "r-reedplain", name: "芦荡平原", terrain: "plain", population: 95, initialEnlightenment: 5, x: 0.51, y: 0.44 },
    { id: "r-ancientforest", name: "古藤雨林", terrain: "forest", population: 75, initialEnlightenment: 2, x: 0.4, y: 0.53 },
    { id: "r-lighthouse", name: "灯塔海角", terrain: "coast", population: 65, initialEnlightenment: 4, x: 0.63, y: 0.72 },
    { id: "r-warmsand", name: "暖沙群岛", terrain: "coast", population: 50, initialEnlightenment: 2, x: 0.68, y: 0.91 },

    // ---------- 东陆（第三章：四大发明西传、大航海） ----------
    { id: "r-amberplain", name: "琥珀平原", terrain: "plain", population: 140, initialEnlightenment: 6, x: 0.73, y: 0.25 },
    { id: "r-inkmountain", name: "墨色群山", terrain: "mountain", population: 60, initialEnlightenment: 3, x: 0.81, y: 0.34 },
    { id: "r-silkvalley", name: "丝绸河谷", terrain: "plain", population: 110, initialEnlightenment: 5, x: 0.75, y: 0.45 },
    { id: "r-pagoda", name: "白塔城邦", terrain: "plain", population: 100, initialEnlightenment: 6, x: 0.86, y: 0.48 },
    { id: "r-icepeak", name: "雪冠山脉", terrain: "mountain", population: 35, initialEnlightenment: 1, x: 0.91, y: 0.27 },
    { id: "r-mistmarsh", name: "迷雾沼泽", terrain: "forest", population: 45, initialEnlightenment: 0, x: 0.91, y: 0.62 },

    // ---------- 极地 ----------
    { id: "r-frostplain", name: "霜原", terrain: "plain", population: 30, initialEnlightenment: 1, x: 0.09, y: 0.14 },
    { id: "r-auroracoast", name: "极光海岸", terrain: "coast", population: 25, initialEnlightenment: 1, x: 0.31, y: 0.09 },
  ],
  routes: [
    // ---------- 西陆陆路 ----------
    { id: "rt-hearth-river", from: "r-hearth", to: "r-riverland", kind: "land", cost: 4 },
    { id: "rt-river-pine", from: "r-riverland", to: "r-pineforest", kind: "land", cost: 5 },
    { id: "rt-hearth-pine", from: "r-hearth", to: "r-pineforest", kind: "land", cost: 6 },
    { id: "rt-pine-cloud", from: "r-pineforest", to: "r-cloudridge", kind: "land", cost: 8 },
    { id: "rt-river-salt", from: "r-riverland", to: "r-saltflat", kind: "land", cost: 6 },
    { id: "rt-cloud-stone", from: "r-cloudridge", to: "r-stonegate", kind: "land", cost: 7 },
    { id: "rt-stone-long", from: "r-stonegate", to: "r-longcoast", kind: "land", cost: 5 },
    { id: "rt-long-salt", from: "r-longcoast", to: "r-saltflat", kind: "land", cost: 6 },
    { id: "rt-hearth-salt", from: "r-hearth", to: "r-saltflat", kind: "land", cost: 9 },

    // ---------- 西陆海路（默认隐藏：需「疫病地图」等节点显现） ----------
    { id: "rt-sea-long-lagoon", from: "r-longcoast", to: "r-lagoonisle", kind: "sea", cost: 6, hiddenByDefault: true },

    // ---------- 跨陆海路 ----------
    {
      id: "rt-sea-long-monsoon",
      from: "r-longcoast",
      to: "r-monsoonbay",
      kind: "sea",
      cost: 9,
      hiddenByDefault: true,
      requiredCardId: "geography-001",
    },
    { id: "rt-sea-monsoon-spice", from: "r-monsoonbay", to: "r-spiceisle", kind: "sea", cost: 5, hiddenByDefault: true },
    { id: "rt-sea-monsoon-lighthouse", from: "r-monsoonbay", to: "r-lighthouse", kind: "sea", cost: 4, hiddenByDefault: true },
    { id: "rt-sea-spice-warmsand", from: "r-spiceisle", to: "r-warmsand", kind: "sea", cost: 7, hiddenByDefault: true },
    { id: "rt-sea-warmsand-aurora", from: "r-warmsand", to: "r-auroracoast", kind: "sea", cost: 11, hiddenByDefault: true },

    // ---------- 中陆陆路 ----------
    { id: "rt-jade-reed", from: "r-jadelake", to: "r-reedplain", kind: "land", cost: 4 },
    { id: "rt-jade-ancient", from: "r-jadelake", to: "r-ancientforest", kind: "land", cost: 6 },
    { id: "rt-jade-monsoon", from: "r-jadelake", to: "r-monsoonbay", kind: "land", cost: 5 },
    { id: "rt-reed-sun", from: "r-reedplain", to: "r-sunplateau", kind: "land", cost: 7 },
    { id: "rt-reed-ancient", from: "r-reedplain", to: "r-ancientforest", kind: "land", cost: 6 },
    { id: "rt-sun-lighthouse", from: "r-sunplateau", to: "r-lighthouse", kind: "land", cost: 6 },
    { id: "rt-lighthouse-monsoon", from: "r-lighthouse", to: "r-monsoonbay", kind: "land", cost: 4 },

    // ---------- 东陆陆路 ----------
    { id: "rt-amber-ink", from: "r-amberplain", to: "r-inkmountain", kind: "land", cost: 5 },
    { id: "rt-amber-silk", from: "r-amberplain", to: "r-silkvalley", kind: "land", cost: 4 },
    { id: "rt-silk-pagoda", from: "r-silkvalley", to: "r-pagoda", kind: "land", cost: 5 },
    { id: "rt-silk-ink", from: "r-silkvalley", to: "r-inkmountain", kind: "land", cost: 6 },
    { id: "rt-ink-ice", from: "r-inkmountain", to: "r-icepeak", kind: "land", cost: 8 },
    { id: "rt-pagoda-mist", from: "r-pagoda", to: "r-mistmarsh", kind: "land", cost: 6 },

    // ---------- 极地陆路 ----------
    { id: "rt-frost-aurora", from: "r-frostplain", to: "r-auroracoast", kind: "land", cost: 4 },
    { id: "rt-frost-cloud", from: "r-frostplain", to: "r-cloudridge", kind: "land", cost: 9 },

    // ---------- 跨陆陆路（商道） ----------
    { id: "rt-pagoda-salt", from: "r-pagoda", to: "r-saltflat", kind: "land", cost: 10 },
    { id: "rt-amber-jade", from: "r-amberplain", to: "r-jadelake", kind: "land", cost: 7 },
  ],
};

/** 已发布的地图集（后续版本在此追加，关卡用 mapId 引用） */
export const MAPS: readonly WorldMap[] = [WORLD_MAP_V1];

export function getMapById(id: string): WorldMap | undefined {
  return MAPS.find((m) => m.id === id);
}
